import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';

import { useGlobalModal } from "../Contexts/GlobalModalContext";
import { ModalActions } from "../Modals/ModalActions";
import { Button } from "./Button";

// TODO: Use this instead of buttons in other pages
interface SaveButtonProps {
  /** Async save handler; should return true on success */
  handleSave: () => Promise<boolean>;
  /** Optional button label, defaults to "Save" */
  buttonLabel?: string;
  /** Confirmation message, defaults to generic */
  confirmMessage?: string;
  /** If false, skips confirmation modal and saves immediately */
  requireConfirmation?: boolean;
  styleClasses?: string
  disabled?: boolean
}

export const SaveButton: React.FC<SaveButtonProps> = ({
  handleSave,
  buttonLabel = 'Save',
  confirmMessage = 'Are you sure you want to save this item? This action cannot be undone.',
  requireConfirmation = true,
  styleClasses,
  disabled = false
}) => {
  const { showModal, hideModal } = useGlobalModal();
  const [isWorking, setIsWorking] = useState(false);

  const SaveModalContent: React.FC = () => {
    const [isModalWorking, setIsModalWorking] = useState(false);

    const onConfirm = async () => {
      setIsWorking(true);
      setIsModalWorking(true);
      try {
        const success = await handleSave();
        if (success) hideModal();
      } catch (error) {
        console.error('Save failed', error);
      } finally {
        setIsWorking(false);
        setIsModalWorking(false);
      }
    };

    return (
      <form>
        <div>
          <h2 className="border-b border-theme-border font-semibold pb-2 mb-8 text-[1.6rem] text-theme-text">
            <span>Confirm Save</span>
            <FontAwesomeIcon
              icon={faSpinner}
              spin
              className={`text-theme-text text-sm transition ease-out mb-2 ml-1 opacity-${isModalWorking ? 100 : 0}`} />
          </h2>
          <p className="text-theme-text">{confirmMessage}</p>
        </div>
        <ModalActions>
          <Button
            variant="ghost"
            onClick={hideModal}
            className="bg-theme-surface hover:bg-theme-surface-2 !font-normal"
          >
            Cancel
          </Button>
          <Button variant="success" isDefaultAction mnemonic="s" onClick={onConfirm}>
            Save
          </Button>
        </ModalActions>
      </form>
    );
  }

  const handleClick = async () => {
    if (requireConfirmation) {
      showModal({ content: <SaveModalContent /> });
      return;
    }

    setIsWorking(true);
    try {
      await handleSave();
    } catch (error) {
      console.error('Save failed', error);
    } finally {
      setIsWorking(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={isWorking || disabled}
      className={`relative px-4 py-2 bg-theme-success hover:bg-theme-success-hover text-white font-bold rounded-sm transition cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed ${styleClasses}`}
    >
      <span className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
        <FontAwesomeIcon
          icon={faSpinner}
          spin
          className={`text-theme-text text-sm transition ease-out opacity-${isWorking ? 100 : 0}`} />
      </span>
      <span className={`${isWorking ? 'opacity-0' : 'opacity-100'} transition ease-out`}>{buttonLabel}</span>
    </button>
  );
};

export default SaveButton;
